import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, X, Check, Linkedin, Mail } from "lucide-react";
import axios from "axios";
import { ThreeBand } from "../components/macaron/ThreeBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAREERS_EMAIL = "hello@themacaron.ai";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: d } }),
};

const roles = [
  {
    title: "Senior AI Engineer",
    details: "Full-time · Remote OK · Equity",
    subject: "Application: Senior AI Engineer",
    description:
      "Build and ship the AI Planogram Generation platform. We're looking for someone who has shipped real ML/AI systems in production.",
  },
  {
    title: "AI Software Engineering Intern",
    details: "Internship · Engineering · Remote OK",
    subject: "Application: AI Engineering Intern",
    description:
      "Work alongside our engineering team on AI planogram generation and computer vision. Help build real production features: data pipelines, model testing, and front-end interfaces.",
  },
  {
    title: "Visual Merchandise Intern",
    details: "Internship · Part-time or Full-time · NYC",
    subject: "Application: Visual Merchandise Intern",
    description:
      "Work directly with the founder on real client projects across visual merchandising strategy, immersive retail, and the AI Consumer Validation product. You'll be in the room for brand pitches, store concept reviews, and product strategy.",
  },
];

const ApplyModal = ({ role, onClose }) => {
  const [mode, setMode] = useState("choose"); // choose | linkedin
  const [form, setForm] = useState({ linkedin_url: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const mailto = `mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(role.subject)}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.linkedin_url) {
      setError("Please paste your LinkedIn profile URL.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/apply`, { role: role.title, ...form });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="mac-overlay" data-testid="apply-modal" onClick={onClose}>
      <div className="mac-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mac-wide-bar" />
        <button className="mac-close" onClick={onClose} data-testid="apply-close-btn" aria-label="Close">
          <X size={20} />
        </button>

        <div className="mac-eyebrow-row">
          <ThreeBand />
          <p className="mac-eyebrow">Apply</p>
        </div>
        <h3 className="mac-card-title" style={{ marginTop: 12 }}>{role.title}</h3>

        {status === "done" ? (
          <div data-testid="apply-success" style={{ textAlign: "center", padding: "22px 0 8px" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--holo)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Check size={24} color="#1F2540" strokeWidth={3} />
            </div>
            <p className="mac-body" style={{ fontSize: 14 }}>Thanks, your application is in. We'll be in touch.</p>
          </div>
        ) : mode === "choose" ? (
          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
            <button className="mac-btn" data-testid="apply-linkedin-btn" style={{ justifyContent: "center" }} onClick={() => setMode("linkedin")}>
              <Linkedin size={15} strokeWidth={2.5} /> Apply via LinkedIn
            </button>
            <a className="mac-btn" data-testid="apply-email-btn" href={mailto} style={{ justifyContent: "center", background: "transparent", color: "var(--white)", border: "1px solid rgba(245,235,224,0.25)", textDecoration: "none" }}>
              <Mail size={15} strokeWidth={2.5} /> Apply via Email
            </a>
          </div>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 22 }} className="flex flex-col gap-4">
            <div>
              <label className="mac-label" htmlFor="a-linkedin">LinkedIn profile URL</label>
              <input id="a-linkedin" className="mac-input" data-testid="apply-linkedin-url" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/you" />
            </div>
            <div>
              <label className="mac-label" htmlFor="a-msg">Message <span style={{ opacity: 0.5, letterSpacing: 0 }}>(optional)</span></label>
              <textarea id="a-msg" className="mac-textarea" data-testid="apply-linkedin-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="A short note: what you've built, why this company, when you can start." />
            </div>
            {error && <p data-testid="apply-error" style={{ color: "var(--pink)", fontSize: 13, margin: 0 }}>{error}</p>}
            <button type="submit" className="mac-btn" data-testid="apply-submit-btn" disabled={status === "sending"} style={{ justifyContent: "center" }}>
              {status === "sending" ? "Sending…" : <>Submit application <ArrowRight size={15} strokeWidth={2.5} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function CareerPathPage() {
  const { openContact } = useOutletContext(); // eslint-disable-line no-unused-vars
  const [activeRole, setActiveRole] = useState(null);

  return (
    <section className="mac-section" data-testid="careers-page" style={{ paddingTop: 120, paddingBottom: 72 }}>
      <div className="mac-container" style={{ position: "relative", zIndex: 1 }}>
        <div className="cta-glow" />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 860 }}>
          <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show">
            <div className="mac-eyebrow-row">
              <ThreeBand />
              <p className="mac-eyebrow">CAREER PATH</p>
            </div>
          </motion.div>

          <motion.h1 variants={fadeUp} custom={0.1} initial="hidden" animate="show" className="mac-display" data-testid="careers-headline" style={{ fontSize: "clamp(40px, 7vw, 68px)", marginTop: 24 }}>
            Build <span className="mac-gradient-text">what's next.</span>
          </motion.h1>

          <motion.p variants={fadeUp} custom={0.25} initial="hidden" animate="show" className="mac-body" style={{ marginTop: 22, maxWidth: 620, fontSize: 16 }}>
            We're assembling a small, founder-led team to build the first AI Visual Merchandising platform for
            premium and luxury brands. If one of these sounds like you, we'd love to hear from you.
          </motion.p>

          <motion.div variants={fadeUp} custom={0.4} initial="hidden" animate="show" style={{ marginTop: 44 }}>
            {roles.map((r, i) => (
              <div className="mac-role-row" key={i} data-testid={`role-${i + 1}`}>
                <div style={{ maxWidth: 640 }}>
                  <h3 className="mac-role-title">{r.title}</h3>
                  <p className="mac-role-type">{r.details}</p>
                  <p className="mac-body" style={{ marginTop: 12, fontSize: 14.5 }}>{r.description}</p>
                </div>
                <button className="mac-btn mac-btn-nav" data-testid={`role-${i + 1}-apply`} onClick={() => setActiveRole(r)} style={{ flexShrink: 0 }}>
                  Apply <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} custom={0.5} initial="hidden" animate="show" data-testid="how-to-apply" style={{ marginTop: 48 }}>
            <div className="mac-eyebrow-row"><ThreeBand /><p className="mac-eyebrow">How to Apply</p></div>
            <p className="mac-body" style={{ marginTop: 16, fontSize: 16, maxWidth: 620 }}>
              Send a short email to{" "}
              <a href={`mailto:${CAREERS_EMAIL}`} style={{ color: "var(--pink)", textDecoration: "none" }}>{CAREERS_EMAIL}</a>{" "}
              with what you've built, why this company, and when you can start.
            </p>
          </motion.div>
        </div>
      </div>

      {activeRole && <ApplyModal role={activeRole} onClose={() => setActiveRole(null)} />}
    </section>
  );
}
